import React, { useEffect, useState } from 'react';
import { getThemeColors } from '../libs/common/themeConfig';

const TOAST_EVENT = 'app-toast';

// Imperative entry point usable from non-React code (e.g. lock logic in common.js).
export const showToast = (message, options = {}) => {
  if (typeof window === 'undefined' || !message) return;
  window.dispatchEvent(
    new CustomEvent(TOAST_EVENT, {
      detail: { message, type: options.type || 'warning', duration: options.duration || 3200 },
    }),
  );
};

const typeAccent = (type, themeColors) => {
  if (type === 'error') return '#ef4444';
  if (type === 'success') return '#22c55e';
  if (type === 'warning') return '#f59e0b';
  return themeColors.accent;
};

export function ToastContainer() {
  const themeColors = getThemeColors();
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    let counter = 0;
    const MAX_VISIBLE = 3;
    const onToast = (event) => {
      const { message, type, duration } = event.detail || {};
      const id = `${Date.now()}-${counter}`;
      counter += 1;
      setToasts((prev) => {
        // Skip if the same message is already on screen, and cap the stack.
        if (prev.some((toast) => toast.message === message)) return prev;
        return [...prev, { id, message, type }].slice(-MAX_VISIBLE);
      });
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
      }, duration);
    };
    window.addEventListener(TOAST_EVENT, onToast);
    return () => window.removeEventListener(TOAST_EVENT, onToast);
  }, []);

  if (!toasts.length) return null;

  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 24,
        transform: 'translateX(-50%)',
        zIndex: 100000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        pointerEvents: 'none',
        width: 'max-content',
        maxWidth: 'min(92vw, 460px)',
      }}
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          style={{
            pointerEvents: 'auto',
            background: themeColors.surface,
            color: themeColors.text,
            border: `1px solid ${themeColors.border}`,
            borderLeft: `4px solid ${typeAccent(toast.type, themeColors)}`,
            borderRadius: 8,
            padding: '10px 14px',
            fontSize: 14,
            lineHeight: 1.35,
            boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
            textAlign: 'center',
            animation: 'toast-in 0.18s ease-out',
          }}
        >
          {toast.message}
        </div>
      ))}
    </div>
  );
}
